import { ReorderableList } from "@/src/components/mobile/ReoardrableListV2/ReordrableList";
import { Positions } from "@/src/components/Positions";
import { AHSwitch, AHText, Box, ListItem, useLayoutContext } from "arkh";
import { useFragment, useLazyLoadQuery } from "react-relay";
import { graphql } from "relay-runtime";
import { RoutineStoreCustomizeSectionsSectionQuery } from "./__generated__/RoutineStoreCustomizeSectionsSectionQuery.graphql";
import { RoutineStoreCustomizeType } from "./RoutineStoreCustomize";
import {
  ReordrableListItem,
  ReordrableListItemProps,
} from "@/src/components/mobile/ReoardrableListV2/ReordrableListItem";

import { useMemo } from "react";

import { LazyQueryNetworkBoundrary } from "@/src/components/mobile/QueryNetworkBoundrary";
import { ScrollView } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useRoutineStoreCustomizeState } from "./State";
import { RoutineStoreCustomizeSectionsSection_task$key } from "./__generated__/RoutineStoreCustomizeSectionsSection_task.graphql";
import { useIntl } from "react-intl";

export function RoutineStoreCustomizeSectionsSection(
  props: RoutineStoreCustomizeType<"RoutineStoreCustomizeSectionsSection">
) {
  return (
    <LazyQueryNetworkBoundrary>
      {(fk) => (
        <InnerRoutineStoreCustomizeSectionsSection {...props} fetchKey={fk} />
      )}
    </LazyQueryNetworkBoundrary>
  );
}

const LIST_ITEM_HEIGHT = 70;

function InnerRoutineStoreCustomizeSectionsSection(
  props: RoutineStoreCustomizeType<"RoutineStoreCustomizeSectionsSection"> & {
    fetchKey: string;
  }
) {
  const intl = useIntl();
  const data = useLazyLoadQuery<RoutineStoreCustomizeSectionsSectionQuery>(
    graphql`
      query RoutineStoreCustomizeSectionsSectionQuery(
        $routineId: ID!
        $storeId: ID
      ) {
        routine(id: $routineId) {
          id
          ...RoutineOccasionScreenRoutineDescription_Routine
          activeTaskList(storeId: $storeId) {
            sections {
              id
              isDisabled
              canBeDisabled
              title
              tasks {
                id
                taskId
                isDisabled
                canBeDisabled
                ...RoutineStoreCustomizeSectionsSection_task
              }
            }
          }
        }
      }
    `,
    {
      routineId: props.route.params.routineId,
      storeId: props.route.params.storeId,
    },
    { fetchPolicy: "store-and-network", fetchKey: props.fetchKey }
  );

  const { state, setState } = useRoutineStoreCustomizeState();
  const section = useMemo(() => {
    return (data.routine?.activeTaskList?.sections ?? []).find(
      (item) => item.id === props.route.params.sectionId
    );
  }, [data.routine?.activeTaskList?.sections, props.route.params.sectionId]);
  const tasks = useMemo(() => {
    return section?.tasks ?? [];
  }, [section?.tasks]);
  const currentTaskOrder = useMemo(() => {
    return (
      state.taskOrder?.[props.route.params.sectionId] ??
      Object.fromEntries(tasks.map((item, i) => [item.taskId, i]))
    );
  }, [state.taskOrder, props.route.params.sectionId, tasks]);
  const sortedTasks = useMemo(() => {
    return tasks.slice().sort((a, b) => {
      const aIndex = currentTaskOrder[a.taskId] ?? 0;
      const bIndex = currentTaskOrder[b.taskId] ?? 0;
      return aIndex - bIndex;
    });
  }, [tasks, currentTaskOrder]);
  const onReorder = (order: Positions) => {
    setState((p) => ({
      ...p,
      taskOrder: {
        ...(p.taskOrder ?? {}),
        [props.route.params.sectionId]: order,
      },
    }));
  };

  const safeAreaInsets = useSafeAreaInsets();
  const layout = useLayoutContext();

  type Task = (typeof sortedTasks)[number];

  const initialDisabledTasks = useMemo(() => {
    return tasks.flatMap((item) =>
      item.taskId && item.isDisabled ? [item.taskId] : []
    );
  }, [tasks]);
  const disabledTasks = useMemo(() => {
    return (
      state.disabledTasks?.[props.route.params.sectionId] ??
      initialDisabledTasks
    );
  }, [state.disabledTasks, initialDisabledTasks, props.route.params.sectionId]);

  if (!data.routine || !section) {
    return null;
  }

  const sectionDisabled = state.disabledSections
    ? state.disabledSections.includes(section.id)
    : section.isDisabled;

  const placeholder = intl.formatMessage({
    defaultMessage: "Section name",
    description:
      "RoutineStoreCustomizeSectionsSection section name placeholder",
  });
  const hasPlaceholder = section.title === "";
  return (
    <ScrollView
      contentContainerStyle={{
        paddingBottom: safeAreaInsets.bottom + layout.space.large,
      }}
    >
      <Box spaceBottom="xlarge">
        <Box
          color="surface"
          cornerRadius="large"
          spaceVertical="medium"
          spaceHorizontal="medium"
          cornerRadiusTopLeft="none"
          cornerRadiusTopRight="none"
          style={{
            marginTop: -1000,
            flexDirection: "row",
            alignItems: "center",
          }}
          insets={{ top: 1000 }}
        >
          <AHText type="title-big" spaceHorizontal="medium" style={{ flex: 1 }}>
            {hasPlaceholder ? placeholder : section?.title}
          </AHText>
          {section.canBeDisabled ? (
            <Box spaceRight="medium">
              <AHSwitch
                value={!sectionDisabled}
                onValueChange={() => {
                  if (!section) {
                    return;
                  }
                  setState((p) => {
                    const isDisabled = p.disabledSections
                      ? p.disabledSections.includes(section.id)
                      : section.isDisabled;
                    return {
                      ...p,
                      disabledSections: isDisabled
                        ? (p.disabledSections?.filter(
                            (id) => id !== section.id
                          ) ?? [])
                        : [...(p.disabledSections ?? []), section.id],
                    };
                  });
                }}
              />
            </Box>
          ) : null}
        </Box>
      </Box>
      <Box spaceHorizontal="medium">
        <ReorderableList<Task>
          items={sortedTasks}
          itemHeight={LIST_ITEM_HEIGHT}
          renderItem={(item, r) => {
            return (
              <TaskListItem
                key={item.taskId}
                task={item}
                reorderableListItemProps={r}
                sectionDisabled={sectionDisabled}
                disabled={disabledTasks.includes(item.taskId)}
                toggleDisabled={() => {
                  setState((p) => {
                    const currentDisabled =
                      p.disabledTasks?.[props.route.params.sectionId] ??
                      initialDisabledTasks;
                    return {
                      ...p,
                      disabledTasks: {
                        ...p.disabledTasks,
                        [props.route.params.sectionId]:
                          currentDisabled.includes(item.taskId)
                            ? currentDisabled.filter((t) => t !== item.taskId)
                            : [...currentDisabled, item.taskId],
                      },
                    };
                  });
                }}
              />
            );
          }}
          keyExtractor={(item) => `${item.taskId}`}
          onReorder={onReorder}
        />
      </Box>
    </ScrollView>
  );
}

function TaskListItem(
  props: ReordrableListItemProps & {
    task: RoutineStoreCustomizeSectionsSection_task$key;
    disabled: boolean;
    sectionDisabled: boolean;
    toggleDisabled: () => void;
  }
) {
  const task = useFragment(
    graphql`
      fragment RoutineStoreCustomizeSectionsSection_task on RoutineTaskListSectionTask {
        task {
          title
        }
        canBeDisabled
      }
    `,
    props.task
  );

  return (
    <ReordrableListItem
      {...props}
      disabled
      reorderableListItemProps={{
        ...props.reorderableListItemProps,
      }}
      border="border-primary"
      right={
        task.canBeDisabled ? (
          <AHSwitch
            value={props.sectionDisabled ? false : !props.disabled}
            disabled={props.sectionDisabled}
            onValueChange={(value) => {
              props.toggleDisabled();
            }}
          />
        ) : null
      }
    >
      <ListItem.Title>{task.task?.title}</ListItem.Title>
    </ReordrableListItem>
  );
}
